import { Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  ErrorState,
  ListView,
  LoadingSkeleton,
  RecordCard,
  useColumns,
} from "../../components/ui";
import { api, type BillSummary } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";

export function BillList() {
  const [bills, setBills] = useState<BillSummary[]>();
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  async function load() {
    try {
      setError(undefined);
      const { bills: loaded } = await api.bills();
      setBills(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load bills.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const columns = useColumns<BillSummary>(
    () => [
      {
        key: "Bill",
        header: "Bill",
        width: "170px",
        render: (bill) => (
          <Text fz={13} fw={600} className="num" truncate>
            {bill.reference}
          </Text>
        ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (bill) => (
          <Text fz={14} truncate>
            {bill.customerName}
          </Text>
        ),
      },
      {
        key: "Date",
        header: "Date",
        width: "130px",
        render: (bill) => (
          <Text fz={13} className="num" c="gray.7">
            {new Date(bill.billDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Text>
        ),
      },
      {
        key: "GST",
        header: "GST",
        width: "140px",
        // The treatment, not the amount: which taxes apply is a property of
        // where the customer is, and it is the thing a return is filed on.
        render: (bill) => (
          <Text fz={13} c="gray.7">
            {bill.gstTreatment === "INTRA_STATE" ? "CGST + SGST" : "IGST"}
          </Text>
        ),
      },
      {
        key: "Total",
        header: "Total",
        width: "150px",
        align: "right",
        render: (bill) => (
          <Text fz={14} fw={600} className="num">
            {formatINR(bill.totalAmount)}
          </Text>
        ),
        // The number, not the formatted string - a spreadsheet cannot add up
        // a rupee sign.
      },
    ],
    [],
  );

  return (
    <Page title="Bills" subtitle="Most recent first" back="/sales" width="wide">
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!bills && !error && <LoadingSkeleton variant="list" count={5} />}
      {bills?.length === 0 && <EmptyState title="Nothing has been billed yet" />}

      {bills && bills.length > 0 && (
        <ListView
          rows={bills}
          columns={columns}
          rowKey={(bill) => bill.id}
          // A bill with no order behind it cannot be opened, so opening a row
          // that has none does nothing rather than going back to this list.
          onOpen={(bill) => {
            if (bill.order) navigate(`/sales/bill/${bill.order.id}`);
          }}
          exportDataset="bills"
          caption="Bills"
          footnote="A bill is never edited. A mistake is corrected by a further document."
          renderCard={(bill) => (
        <RecordCard
          key={bill.id}
          // A bill with no order behind it cannot be opened, so it is not a
          // link at all rather than one that goes back to this same list.
          {...(bill.order ? { to: `/sales/bill/${bill.order.id}` } : {})}
          primary={bill.reference}
          secondary={bill.customerName}
          action={
            <span className="num" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {formatINR(bill.totalAmount)}
            </span>
          }
          meta={`${new Date(bill.billDate).toLocaleDateString()} · ${
            bill.gstTreatment === "INTRA_STATE" ? "CGST + SGST" : "IGST"
          }`}
        />
          )}
        />
      )}
    </Page>
  );
}
